import backendV2Service from './backend-v2.service';

/**
 * User Enrollment interface
 */
export interface UserEnrollment {
  id: number;
  status: string;
  bookuserId: number;
  schoolId: number;
  courseId?: number;
  periodarrangementId: number;
  klPriorregistrationId?: number;
  createdDate: string;
  updatedDate?: string;
}

/**
 * DTO for creating/updating user enrollments
 */
export interface UserEnrollmentDto {
  status: string;
  bookuserId: number;
  schoolId: number;
  courseId?: number;
  periodarrangementId: number;
  klPriorregistrationId?: number;
}

/**
 * DTO for switching user enrollments
 */
export interface SwitchUserEnrollmentDto {
  bookUserIds: number[];
  newCourseId?: number;
  newSchoolId?: number;
  currentSchoolId: number;
}

/**
 * Legacy response format (maintained for compatibility)
 */
export interface UserEnrollmentResponse {
  status: string;
  content: unknown;
}

const unwrap = <T>(request: Promise<{ data: T }>) => request.then(({ data }) => data);

/**
 * User Enrollment Service
 * Migrated from main-backend to backend-v2
 */
export default {
  /**
   * Switch user enrollments between schools/courses
   * POST /user-enrollments/switch
   */
  switchUserEnrollment: (data: SwitchUserEnrollmentDto): Promise<UserEnrollmentResponse> => unwrap(backendV2Service.post<UserEnrollmentResponse>('/user-enrollments/switch', data)),

  /**
   * Update enrollment status by book user ID
   * PUT /user-enrollments/update-status/:bookUserId
   */
  updateStatusByBookUserId: (
    bookUserId: number | string,
    status?: string,
  ): Promise<UserEnrollmentResponse> => unwrap(
    backendV2Service.put<UserEnrollmentResponse>(
      `/user-enrollments/update-status/${bookUserId}${status ? `?status=${status}` : ''}`,
      {},
    ),
  ),

  /**
   * Get all user enrollments
   * GET /user-enrollments
   */
  findAll: (): Promise<UserEnrollmentResponse> => unwrap(backendV2Service.get<UserEnrollmentResponse>('/user-enrollments')),

  /**
   * Get user enrollment by ID
   * GET /user-enrollments/:id
   */
  findById: (id: number | string): Promise<UserEnrollmentResponse> => unwrap(backendV2Service.get<UserEnrollmentResponse>(`/user-enrollments/${id}`)),

  /**
   * Create a new user enrollment
   * POST /user-enrollments
   */
  create: (data: UserEnrollmentDto): Promise<UserEnrollmentResponse> => unwrap(backendV2Service.post<UserEnrollmentResponse>('/user-enrollments', data)),

  /**
   * Update an existing user enrollment
   * PUT /user-enrollments/:id
   */
  update: (id: number | string, data: Partial<UserEnrollmentDto>): Promise<UserEnrollmentResponse> => unwrap(backendV2Service.put<UserEnrollmentResponse>(`/user-enrollments/${id}`, data)),

  /**
   * Delete a user enrollment
   * DELETE /user-enrollments/:id
   */
  delete: (id: number | string): Promise<void> => unwrap(backendV2Service.delete<void>(`/user-enrollments/${id}`)),
};
